export type UploadStage = 'initialize' | 'create' | 'list' | 'part' | 'complete' | 'verify' | 'abort';

const labels: Record<UploadStage, string> = {
	initialize: 'Preparing storage access',
	create: 'Starting the video upload',
	list: 'Checking uploaded parts',
	part: 'Uploading a video part',
	complete: 'Finalizing the video',
	verify: 'Verifying the uploaded video',
	abort: 'Cancelling the upload',
};

function field(error: unknown, key: string): unknown {
	return error && typeof error === 'object' && key in error ? Reflect.get(error, key) : undefined;
}

export function isUploadPaused(error: unknown): boolean {
	return field(error, 'code') === 'PAUSED' || field(error, 'name') === 'AbortError';
}

export function uploadStatus(error: unknown): number | undefined {
	const metadata = field(error, '$metadata');
	const value = field(error, 'status') ?? field(error, 'statusCode') ?? field(metadata, 'httpStatusCode');
	return typeof value === 'number' ? value : undefined;
}

export class UploadTransportError extends Error {
	readonly code?: string;
	readonly status?: number;
	readonly requestId?: string;
	readonly safeToRetryCreate: boolean;

	constructor(readonly stage: UploadStage, readonly cause: unknown) {
		const status = uploadStatus(cause);
		const name = field(cause, 'code') ?? field(cause, 'name');
		const code = typeof name === 'string' && name !== 'Error' ? name : undefined;
		const message = field(cause, 'message');
		const raw = typeof message === 'string' ? message : typeof cause === 'string' ? cause : 'Storage request failed.';
		let detail = raw.slice(0, 500);
		if (status === 401 || status === 403) detail += ' Check the storage credentials and multipart upload permissions.';
		else if ((cause instanceof TypeError && /failed to fetch|network|load failed|fetch failed/i.test(cause.message)) || code === 'NetworkError') {
			detail += stage === 'abort' ? ' Check the connection and ensure storage CORS allows DELETE for this admin origin.' :
				' Check the connection and storage CORS settings.';
		}
		super(`${labels[stage]} failed${status ? ` (HTTP ${status})` : ''}${code ? ` [${code}]` : ''}: ${detail}`);
		this.name = 'UploadTransportError';
		this.code = code;
		this.status = status;
		const requestId = field(field(cause, '$metadata'), 'requestId');
		this.requestId = typeof requestId === 'string' ? requestId : undefined;
		this.safeToRetryCreate = stage === 'initialize' || (stage === 'create' && status !== undefined && status >= 400 && status < 500 && status !== 408);
		Object.setPrototypeOf(this, UploadTransportError.prototype);
	}
}

export function uploadFailureMessage(error: unknown): string {
	const message = field(error, 'message');
	if (typeof message === 'string' && message) return message;
	if (typeof error === 'string' && error) return error;
	return 'The storage request failed without an error description.';
}
